import LegalLayout, { LegalSection, LegalSubSection } from '@/components/LegalLayout';
import { Link } from 'react-router-dom';
import SeoHead from '@/components/SeoHead';

export default function DataProtection() {
  return (
    <>
      <SeoHead
        title="Data Protection Policy"
        description="How WriteAssess fulfils its obligations as a data controller and processor under UK GDPR."
        canonical="/data-protection"
      />
      <LegalLayout title="Data Protection Policy" lastUpdated="2 May 2026">
      <LegalSection title="1. Overview">
        <p>
          WriteAssess is committed to handling personal data in accordance with UK GDPR and the Data Protection Act
          2018. This policy sets out how we fulfil our obligations as both a data controller (for teacher and
          account data) and a data processor (for pupil data processed on behalf of schools).
        </p>
        <p>
          This policy should be read alongside our{' '}
          <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link> and{' '}
          <Link to="/safeguarding" className="text-primary hover:underline">Safeguarding Policy</Link>.
        </p>
      </LegalSection>

      <LegalSection title="2. Data Controller vs Data Processor">
        <LegalSubSection title="2.1 Where we are the Data Controller">
          <p>
            WriteAssess acts as data controller for personal data relating to its own customers — specifically,
            teacher and administrator account information (name, email, billing details, usage data). We determine
            the purposes and means of processing this data and are directly accountable to those individuals under
            UK GDPR.
          </p>
        </LegalSubSection>
        <LegalSubSection title="2.2 Where we are the Data Processor">
          <p>
            For pupil data (names, year groups, writing sample images, and assessment results) uploaded to the
            platform by teachers, WriteAssess acts as a data processor on behalf of the school, which is the data
            controller. Schools determine what pupil data is entered into the system and are responsible for
            ensuring they have lawful basis to do so.
          </p>
          <p>
            As data processor, we process pupil data only on the documented instructions of the school, and do not
            use it for any other purpose.
          </p>
        </LegalSubSection>
      </LegalSection>

      <LegalSection title="3. Data Processing Agreement">
        <p>
          Schools using WriteAssess on a paid plan may request a Data Processing Agreement (DPA) setting out the
          respective obligations of WriteAssess (processor) and the school (controller) under Article 28 UK GDPR.
          Contact us at{' '}
          <Link to="/contact" className="text-primary hover:underline">our contact form</Link>{' '}
          to request a DPA.
        </p>
        <p>
          Free-plan users are covered by the data processing terms embedded in our{' '}
          <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>.
        </p>
      </LegalSection>

      <LegalSection title="4. Lawful Basis for Processing Pupil Data">
        <p>
          Schools should satisfy themselves that they have an appropriate lawful basis for sharing pupil data with
          WriteAssess. This will typically be:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <strong className="text-foreground">Public task (Art. 6(1)(e)):</strong> processing is necessary for
            the performance of a task carried out in the public interest — specifically, the statutory duty to
            assess and report on pupil attainment.
          </li>
          <li>
            <strong className="text-foreground">Legitimate interests (Art. 6(1)(f)):</strong> where the school
            uses WriteAssess for internal formative assessment and professional development.
          </li>
        </ul>
        <p>
          Schools should record their lawful basis in their own records of processing activities (ROPA) as required
          by Article 30 UK GDPR.
        </p>
      </LegalSection>

      <LegalSection title="5. Minimisation and Anonymisation">
        <p>
          We encourage schools to minimise the pupil data entered into the system:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            WriteAssess supports <strong className="text-foreground">anonymisation options</strong> — teachers can
            choose to display pupils by full name, first initial only, or initials only within the platform.
          </li>
          <li>
            Writing sample images should not include photographs of pupils' faces or other unnecessary identifying
            information.
          </li>
          <li>
            Special category data (e.g. data revealing health conditions or SEND status) should not be entered into
            free-text fields.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="6. International Data Transfers">
        <p>
          Some of our sub-processors are based outside the UK/EEA (see our{' '}
          <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link> for the full list).
          Where data is transferred internationally, we rely on:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>UK International Data Transfer Agreements (UK IDTA), or</li>
          <li>Standard Contractual Clauses (SCCs) as adopted by the European Commission and recognised under UK law</li>
        </ul>
        <p>
          Schools that require confirmation of specific transfer mechanisms for their DPA should contact{' '}
          <Link to="/contact" className="text-primary hover:underline">our contact form</Link>.
        </p>
      </LegalSection>

      <LegalSection title="7. Security Measures">
        <p>We maintain the following technical and organisational security measures:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>All data is encrypted in transit using TLS 1.2 or higher</li>
          <li>Data at rest is encrypted by our hosting provider (Supabase / AWS)</li>
          <li>Pupil writing sample images are stored in a private bucket — not publicly accessible</li>
          <li>Access to production data is restricted to authorised personnel only</li>
          <li>Row-Level Security (RLS) policies ensure teachers can only access data belonging to their school</li>
          <li>Authentication is managed via Supabase Auth with email verification</li>
          <li>We conduct periodic reviews of access controls and security configurations</li>
        </ul>
      </LegalSection>

      <LegalSection title="8. Data Breach Procedures">
        <p>
          In the event of a personal data breach, WriteAssess will:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Contain the breach as quickly as possible</li>
          <li>Assess the risk to individuals</li>
          <li>
            Notify affected schools without undue delay and within 72 hours of becoming aware, where required
          </li>
          <li>
            Report to the ICO within 72 hours if the breach is likely to result in a risk to individuals' rights
            and freedoms
          </li>
          <li>Document the breach and our response</li>
        </ul>
        <p>
          Schools should also follow their own breach notification procedures and notify their Data Protection
          Officer (DPO) where applicable.
        </p>
      </LegalSection>

      <LegalSection title="9. Retention and Deletion">
        <p>
          We retain pupil data and associated content only for the period necessary to deliver the service.
          Our full retention schedule, covering all data categories, is published in our{' '}
          <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>. The key periods
          relevant to schools as data controllers are:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <strong className="text-foreground">Pupil records:</strong> deleted or anonymised within{' '}
            <strong className="text-foreground">90 days</strong> of subscription cancellation or account closure,
            unless earlier deletion is requested.
          </li>
          <li>
            <strong className="text-foreground">Writing samples, assessments, and feedback:</strong> retained for{' '}
            <strong className="text-foreground">3 academic years</strong> from the date of submission to support
            statutory moderation and safeguarding obligations, then deleted. Schools may request earlier deletion at any time.
          </li>
          <li>
            <strong className="text-foreground">Assessment chat history:</strong> retained for{' '}
            <strong className="text-foreground">3 academic years</strong>, aligned with assessment data.
          </li>
        </ul>
        <p>
          Schools may request deletion of all pupil data at any time by contacting us via our{' '}
          <Link to="/contact" className="text-primary hover:underline">contact form</Link>. Individual teachers
          on personal plans may delete their account and all associated data directly from their account settings.
        </p>
      </LegalSection>

      <LegalSection title="10. Sub-processor Changes">
        <p>
          We will provide reasonable notice (at least 14 days) to schools before making any changes to our
          sub-processors that may affect how pupil data is processed. Schools that object to a sub-processor change
          may terminate their subscription in accordance with our Terms of Service.
        </p>
      </LegalSection>

      <LegalSection title="11. Contact">
        <p>
          For data protection enquiries, to request a DPA, or to exercise data subject rights on behalf of pupils,
          contact us at{' '}
          <Link to="/contact" className="text-primary hover:underline">our contact form</Link>.
        </p>
      </LegalSection>
    </LegalLayout>
    </>
  );
}
